import type { LandingPartnerPublic, PortfolioClientLogo } from '@repo/api-contracts';
import type { ReactElement } from 'react';
import { Link } from 'react-router';
import { clientLogoItems } from './client-logo-items';

interface HomeClientLogosProps {
  /** 고객사명·로고가 있는 공개 사례 — 배열 순서가 노출 순서다. 운영자 로고가 없을 때 쓴다. */
  readonly items: readonly PortfolioClientLogo[];
  /** 운영자가 랜딩 관리에서 등록한 기업 로고 — 하나라도 있으면 이것만 보여 준다. */
  readonly partners?: readonly LandingPartnerPublic[];
}

const LOGO_CLASS =
  'flex h-12 w-full items-center justify-center opacity-80 transition-opacity hover:opacity-100 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

/**
 * HOME 함께한 브랜드 — 다른 구역과 같은 왼쪽 정렬 머리말 아래 로고를 여섯 칸(태블릿 네 칸, 폰 세 칸) 격자로 늘어놓는다.
 * 운영자가 등록한 기업 로고가 있으면 그것만(링크 없음), 없으면 포트폴리오 사례의 고객사 로고를
 * 그 사례 상세로 가는 링크로 보여 준다. 접근성 이름은 기업명(고객사명)이다.
 * 로고가 하나도 없으면 섹션을 그리지 않는다(지어내지 않는다).
 */
export const HomeClientLogos = ({
  items,
  partners = [],
}: HomeClientLogosProps): ReactElement | null => {
  const logos = clientLogoItems(partners, items);
  if (logos.length === 0) return null;

  return (
    <section aria-labelledby="home-client-logos-heading" className="w-full px-5 sm:px-8 xl:px-14">
      {/* 큰 제목 없이 다른 구역의 영문 라벨과 같은 줄만 왼쪽에 둔다 — 로고가 내용이다. */}
      <h2 id="home-client-logos-heading" className="text-base text-muted-foreground sm:text-lg">
        Partners
      </h2>
      <ul className="mt-10 grid grid-cols-3 gap-x-6 gap-y-10 sm:mt-14 sm:grid-cols-4 lg:grid-cols-6">
        {logos.map((logo) => (
          <li key={logo.id} className="flex items-center justify-center">
            {logo.href === null ? (
              <div className={LOGO_CLASS}>
                <img
                  src={logo.logoUrl}
                  alt={logo.name}
                  loading="lazy"
                  className="max-h-12 max-w-[9rem] object-contain"
                />
              </div>
            ) : (
              <Link to={logo.href} aria-label={`${logo.name} 제작 사례`} className={LOGO_CLASS}>
                <img
                  src={logo.logoUrl}
                  alt={logo.name}
                  loading="lazy"
                  className="max-h-12 max-w-[9rem] object-contain"
                />
              </Link>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
};
